import { AsyncPipe, DecimalPipe, NgClass } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { ChangeDetectionStrategy, Component, OnInit, input, inject } from '@angular/core';
import { FormsModule, ReactiveFormsModule, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { ClientService, ClaimService, SalesReturnService } from '@fitness-force/api';
import { AppErrors } from '@fitness-force/models';
import { ErrorMsgComponent } from '@fitness-force/errors';;
import { DataGridComponent, EmptyStateContainerComponent } from '@fitness-force/grid';
import { DateFormatedComponent } from '@fitness-force/grid-utility';
import { ActionsComponent } from '@fitness-force/grid-utility/action';
import { CurrencyComponent } from '@fitness-force/grid-utility/currency';
import type { Claim, ClientSalesReturnList, SalesReturn, TENANTSINTERFACE, voidSalesReturnRequest } from '@fitness-force/models';
import { PdfType } from '@fitness-force/models';
import { NumberPrefixComponent } from '@fitness-force/common-number-prefix';
import { Store } from '@ngrx/store';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TENANTS_INFO } from '@fitness-force/state';
import { PdfViewerModule } from 'ng2-pdf-viewer';
import { ToastMessageService } from '@fitness-force/api';
import { AvatarModule } from 'primeng/avatar';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { DividerModule } from 'primeng/divider';
import { SelectModule } from 'primeng/select';
import { PopoverModule } from 'primeng/popover';
import { RippleModule } from 'primeng/ripple';
import { StyleClassModule } from 'primeng/styleclass';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { TimelineModule } from 'primeng/timeline';
import { BehaviorSubject, Observable } from 'rxjs';
import { SkeletonModule } from 'primeng/skeleton';
import { SalesReturnStatusComponent } from '../sales-return-status/sales-return-status.component';

@Component({
  selector: 'sales-return-tab',
  templateUrl: './sales-return.component.html',
  styleUrls: ['./sales-return.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    DividerModule,
    TimelineModule,
    TableModule,
    ReactiveFormsModule,
    FormsModule,
    ButtonModule,
    RippleModule,
    SelectModule,
    AvatarModule,
    TagModule,
    StyleClassModule,
    DialogModule,
    PdfViewerModule,
    PopoverModule,
    DataGridComponent,
    ErrorMsgComponent,
    SkeletonModule,
    TranslateModule, AsyncPipe, DecimalPipe, NgClass],
})

export class SalesReturnComponent implements OnInit {
  private store = inject(Store);
  private translate = inject(TranslateService);
  private clientService = inject(ClientService);
  private toastMessageService = inject(ToastMessageService);
  private fb = inject(UntypedFormBuilder);
  private http = inject(HttpClient);
  private claimService = inject(ClaimService);
  private salesReturnService = inject(SalesReturnService);

  readonly clientId = input(0);
  readonly tenantId = input(0);
  readonly subscriptionId = input(0);
  readonly showTableActions = input(true);
  readonly showHeader = input(true);

  salesReturnData$ = new BehaviorSubject<SalesReturn[]>([] as SalesReturn[]);
  salesReturnTablecolumns$ = new BehaviorSubject<any>([]);
  salesReturnClientDataObject: BehaviorSubject<SalesReturn> | any;
  selectallsalesReturnData = false;

  claim$: Observable<Claim[]> = new Observable();
  _filterStaff = new BehaviorSubject<number[]>([0]);
  _sessionOwner = new BehaviorSubject<number[]>([0]);
  accessedTenants:any;
  btnLoading=false;
  emptyStateComponent$ = new BehaviorSubject<any>(EmptyStateContainerComponent);
  showBulkActionBar: boolean = false;
  salesReturnId:number=0;
  salesReturnDetails:SalesReturn = {} as SalesReturn;
  showSalesReturnVoid:boolean=false;
  voidSalesReturnForm: UntypedFormGroup = new UntypedFormGroup({});
  invoiceId: number = 0;
  number_prefix:string="";
  showInvoicePopup:boolean=false;

  showSalesReturnPopup = new BehaviorSubject(false);
  pdfSrc = new BehaviorSubject<string>('');
  loading:boolean = false;

  ngOnInit(): void {
    this.store.select(TENANTS_INFO).subscribe((tenant: TENANTSINTERFACE) => {
      this.accessedTenants = tenant.accessed_tenant;
    });
    this.getSalesReturn();
  }

  ngAfterViewInit() {
    this.voidSalesReturnForm = this.fb.group({
      void_reason: ['', [Validators.required]],
    });
  }

  getSalesReturn() {
    this.loading = true;
    this.clientService.getClientSalesReturns(this.clientId()).subscribe({
      next: (data: ClientSalesReturnList) => {
        this.setClientSalesReturns(data.items ? data.items : []);
        this.loading = false;
      },
      error: (error: AppErrors) => {
        this.setClientSalesReturns([]);
        this.loading = false;
      },
    });
  }

  setClientSalesReturns(SalesReturns: SalesReturn[]) {
    SalesReturns.map((i) => {
      i.invoiceNumbers = [];
      i.invoiceNumbers.push(i.invoice_number_prefix);
    });
    this.salesReturnData$.next(SalesReturns);
    this.selectallsalesReturnData = false;
    this.GetSalesReturnTableColumns();
  }

  GetSalesReturnTableColumns() {
    this.translate
      .get([
        'SALES_RETURN.COLUMNS.DATE',
        'SALES_RETURN.COLUMNS.ID',
        'SALES_RETURN.COLUMNS.INVOICE',
        'SALES_RETURN.COLUMNS.AMOUNT',
        'SALES_RETURN.COLUMNS.STATUS'
  ])
      .subscribe((translations) => {
        this.ColumnsInvoiceTableTransalation(translations);
      });
  }

  ColumnsInvoiceTableTransalation(translations: any) {
    const Columns = {
      DATE: translations['SALES_RETURN.COLUMNS.DATE'],
      INVOICE: translations[ 'SALES_RETURN.COLUMNS.INVOICE'],
      ID: translations[ 'SALES_RETURN.COLUMNS.ID'],
      AMOUNT: translations['SALES_RETURN.COLUMNS.AMOUNT'],
      STATUS: translations['SALES_RETURN.COLUMNS.STATUS']
    };

    this.salesReturnTablecolumns$.next([
      {
        title: Columns.DATE,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'sales_return_date',
        type: 'component',
        data: {
          name: DateFormatedComponent,
          additionData: true,
          aData: { KeyName: 'sales_return_date', format: 'd MMM y' },
        },
      },
      {
        title: Columns.ID,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'number_prefix',
        type: 'component',
        data: {  
          name: NumberPrefixComponent,
          additionData: true,
          aData: { fetch_key: 'number_prefix', type: PdfType.SalesReturn,id:'id' }
        },
      },
      {
        title: Columns.INVOICE,
        classes: 'py-3 white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'number_prefix',
        type: 'component',
        data: {  
          name: NumberPrefixComponent,
          additionData: true,
          aData: { fetch_key: 'invoice_number_prefix', type: PdfType.Invoice, id:'invoice_id' }
        },
      },
      {
        title: Columns.AMOUNT,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'total_amount_in_minor_currency',
        type: 'component',
        data: {
          name: CurrencyComponent,
          fetch_key: 'total_amount_in_minor_currency',
        },
      },
      {
        title: Columns.STATUS,
        classes: ' py-3 text-600 w-15rem white-space-nowrap',
        sortable: true,
        filterable: true,
        selectable: false,
        dataProperty: 'is_void',
        type: 'component',
        data: {
          name: SalesReturnStatusComponent,
          additionData: true,
          aData: { fetch_key: 'is_void' },
        }
      }
    ]);

    if (this.showTableActions()) {
      this.salesReturnTablecolumns$.value.push({
        title: '',
        classes: 'py-2  white-space-nowrap',
        dataProperty: 'actions',
        type: 'component',
        data: {
          name: ActionsComponent,
          additionData: true,
          aData: [
            'salesreturnvoid',
            'sendemail',
            'print'
          ],
        },
      });
    }
  }

  handleSelectAll(selection: any) {
    if (selection.checked) {
      this.showBulkActionBar = true;
    } else {
      this.showBulkActionBar = false;
    }
  }

  handleEvent(e: any) {
    switch (e.action) {
      case 'salesreturnvoid':
        {
          this.salesReturnId = e.columnData.id;
          this.salesReturnDetails = e.columnData;
        }
    }
  }

  handleTabAction(event:{closePopup:boolean, reloadData:boolean})
  {
    console.log(event);
  }

  onActionEvent(event: any) {
    switch (event.action) {
      case 'salesreturnvoid':
        {
          this.salesReturnId = event.columnData.id;
          this.salesReturnDetails = event.columnData;
          if (!this.salesReturnDetails.is_void)
            this.showSalesReturnVoid = true;
        }
       break;
      case 'openinvoice':
        {
          this.GetInvoiceDetails(event.columnData);
        }
          break;
      case 'sendemail':
        {
          this.salesReturnDetails = event.columnData;
          this.SendSalesReturnPdf(false).then((res) => {
            console.log(res);
          });
        }
        break;
      case 'print':
        {
          this.salesReturnId = event.columnData.id;
          this.salesReturnDetails = event.columnData;
          this.GetSalesReturnPdf(false).then((res) => {
            console.log(res);
            this.printInvoice();
          });
        }
        break;
      case 'updatedata':
        {
          this.getSalesReturn();
        }
          break;
  }
  }

  GetInvoiceDetails(invoiceNumber: string) {
   const invoiceData = this.salesReturnData$.value.find(
      (i) => i.invoice_number_prefix == invoiceNumber
    )!;
    this.invoiceId = invoiceData.invoice_id;
    this.number_prefix = invoiceData.invoice_number_prefix;
    this.showInvoicePopup = true;
  }

  GetSalesReturnPdf(showSalesReturn: boolean) {
    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      this.salesReturnService.GetSalesReturnPrint(this.clientId(), this.salesReturnDetails.invoice_id,this.salesReturnDetails.id)
        .subscribe({
          next: (data: any) => {
            this.pdfSrc.next(data.url);
            resolve(true);
          },
          error: (error: AppErrors) => {
              this.ShowToastErrors(error);
              resolve(false);
          },
        });
    });
    return promise;
  }

  SendSalesReturnPdf(showSalesReturn: boolean) {
    const promise: Promise<boolean> = new Promise((resolve, reject) => {
      const channels:any[]=[];
      channels.push("Email");
      this.salesReturnService.SendSalesReturnPdf(this.clientId(), this.salesReturnDetails.invoice_id,this.salesReturnDetails.id,channels)
        .subscribe({
          next: (data: any) => {
            this.pdfSrc.next(data);
            this.translate
            .get('TRANSACTION.MSG.INVOICE_MAIL_MSG')
            .subscribe((translation) => {
              this.toastMessageService.showSuccess(translation);
            });
            resolve(true);
          },
          error: (error: AppErrors) => {
            if(error.originalError.status == 200)
            {
                this.toastMessageService.showSuccess(error.originalError.error.text);
                resolve(true);
            }
            else{
              this.ShowToastErrors(error);
              resolve(false);
            }
           
          },
        });
    });
    return promise;
  }

  printInvoice() {
    window.open(this.pdfSrc.value, '_blank');
  }

  deleteSalesReturn(form: UntypedFormGroup) {
    this.btnLoading= true;
    if (form.valid) {
      const voidRequest: voidSalesReturnRequest = {} as voidSalesReturnRequest;
      voidRequest.void_reason = form.value.void_reason;
      let tenantId:number=0;
      if(this.accessedTenants.find((o: { id: number; }) => o.id == this.salesReturnDetails.tenant_id))
         tenantId =  this.salesReturnDetails.tenant_id;
      else tenantId= this.tenantId();
      this.salesReturnService.VoidSalesReturn(tenantId, this.clientId(), this.salesReturnId, voidRequest)
        .subscribe({
          next: () => {
            this.translate
              .get('SALES_RETURN.MSG.SALES_RETURN_VOID_MSG')
              .subscribe((translation) => {
                this.toastMessageService.showSuccess(translation);
              });
            this.voidSalesReturnForm.reset();
            this.salesReturnDetails.is_void = true;
            const salesReturnData = this.salesReturnData$.value;
            salesReturnData.forEach((inv) => {
              if (inv.id == this.salesReturnDetails.id)
                inv.is_void = true;
            });
            this.salesReturnData$.next(salesReturnData);
            this.GetSalesReturnTableColumns();
            this.btnLoading= false;
            this.showSalesReturnVoid = false;
          },
          error: (error: AppErrors) => {
            this.ShowToastErrors(error);
            this.btnLoading= false;
          },
        });
    } else {
      Object.keys(this.voidSalesReturnForm.controls).forEach((field) => {
        const control = this.voidSalesReturnForm.get(field);
        control?.markAsTouched({ onlySelf: true });
      });
      this.btnLoading= false;
    }
  }

  ShowToastErrors(error: any) {
    if (error.originalError.error && error.originalError.error.length > 0) {
      error.originalError.error.forEach((e: any) => {
        this.toastMessageService.showErrorMessage(e.errorMessage);
      });
    } else {
      this.toastMessageService.showErrorMessage(error.originalError.error.errorMessage);
    }
  }

  closeInvoicePopup(event:any)
  {
    this.showInvoicePopup=false;
  }

  closeSalesReturnPopup()
  {
    this.showSalesReturnPopup.next(false);
  }
  
  get VoidReasonFormControl(): UntypedFormControl {
    return this.voidSalesReturnForm.controls['void_reason'] as UntypedFormControl;
  }

}